import clsx from "clsx";
import Heading from "components/Elements/Heading";

import {
  closeAllBarchartTooltips,
  CloseSelectedInsight,
  fetchInsights,
  InsightsTickIndicator,
  InsightsWithinTooltip,
  OpenSelectedInsight,
  RenderBackground,
  RenderTooltipInsights,
} from "components/Utils/InsightsUtils";

import {
  CustomizedYAxisTick,
  CustomizedYAxisTickRight,
  formatDateFromData,
  getAllKeys,
  getImageSrc,
  isDateFromDataInISOFormat,
  MAIN_COLORS,
  Metric,
  MetricTable,
  selectFormat,
  StandardTooltip,
  truncate,
} from "components/Utils/Utils";

import { XRayTooltip } from "components/Utils/XrayUtils";
import { getXrayTooltipPosition } from "helpers/DOM";

import moment from "moment";
import React, { Component, Fragment } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import missing_image from "./../../i/missing_image.svg";

const COLORS = [MAIN_COLORS[1], MAIN_COLORS[0]];

const nonStackedBars = ["net installs", "net starts"];

const metricBar = (cell) => (
  <MetricTable {...cell} graph={cell.trends} graphType="bar" />
);

class LeeActivations extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);

    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  getExportData = () => {
    let { data } = this.props.data;
    return {
      data: data,
      opts: {
        type: "chart",
        date: {
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(this.props.data.data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        <span
          className="legend__icon"
          style={{
            background: nonStackedBars.includes(entry) ? "#868E96" : COLORS[i],
          }}
        ></span>
        <span
          className="legend__text"
          style={{
            color: nonStackedBars.includes(entry) ? "#868E96" : COLORS[i],
          }}
        >
          {entry}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    const sortedKeys = getAllKeys(data, ["date"]).filter(
      (k) => !nonStackedBars.includes(k)
    );
    return this.legendElement([...new Set(sortedKeys)]);
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  renderBars = (data) => {
    let bars = [];
    let labels = [];
    const sortedKeys = getAllKeys(data, ["date"]); // this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data?.length <= 7;

    [...new Set(sortedKeys)].forEach((bar, i) => {
      if (nonStackedBars.includes(bar)) {
        return;
      }

      bars.push(
        <Bar
          key={i}
          dataKey={bar}
          barSize={isFixedBarWidth ? 35 : null}
          fill={COLORS[i]}
          isAnimationActive={false}
          // only first bar needs background
          cursor="pointer"
          background={
            i === 0 ? (
              <RenderBackground
                insights={insights}
                showIndicator={!this.state.tooltip.show}
                node={this.node}
                i={i}
              />
            ) : null
          }
          onClick={(d) => {
            const label = labels.filter((l) => l.date === d.date);
            const extra = sortedKeys
              .map((k, j) => ({
                key: k,
                value: d[k],
                color: MAIN_COLORS[j],
              }))
              .filter((o) => o.value !== undefined);
            d.extra = extra;
            d.insight = label.length ? label[0] : null;
            d.posts = d.posts || [];
            d.topLists = this.props.data.topLists;
            this.clickBar(d, bar);
          }}
        >
          {data.map(
            (entry, index) =>
              entry && (
                <Cell
                  key={`cell-${bar}-${index}`}
                  fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                ></Cell>
              )
          )}
        </Bar>
      );
    });

    return bars;
  };

  render() {
    let { data, title } = this.props.data;
    const { url } = this.props;
    const isWidgetForRate = url.includes("installs-rate");

    const { widgetsInsights } = this.state;
    const insightsData = widgetsInsights.length > 0;

    const preparedData = formatDateFromData(data);
    const allKeys = getAllKeys(data, ["date"]);

    return (
      <div ref={this.divPosition}>
        <div className="row">
          {data.installs && (
            <div className="mb-4 col-6">
              <Heading size={6} className={"mb-1"}>
                New Installs
              </Heading>
              <Metric {...data.installs} size={"lg"} format={"0,00"} />
            </div>
          )}

          {data.uninstalls && (
            <div className="mb-4 col-6">
              <Heading size={6} className={"mb-1"}>
                Uninstalls
              </Heading>
              <Metric {...data.uninstalls} size={"lg"} format={"0,00"} />
            </div>
          )}

          {data.uninstalls_rate && (
            <div className="mb-4 col-6">
              <Heading size={6} className={"mb-1"}>
                Uninstall Rate
              </Heading>
              <Metric {...data.uninstalls_rate} size={"lg"} format={"%"} />
            </div>
          )}

          {data.cvr && (
            <div className="mb-4 col-6">
              <Heading size={6} className={"mb-1"}>
                % CVR
              </Heading>
              <Metric {...data.cvr} size={"lg"} format={"%"} />
            </div>
          )}
        </div>
        <div>{this.renderLegends(preparedData)}</div>
        <div className="mt-2 d-flex">
          <ResponsiveContainer height={370} width="100%">
            <ComposedChart
              data={preparedData}
              margin={{ top: 8, right: 0, bottom: 15, left: -38 }}
              stackOffset="sign"
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          let total = "";

                          if (tooltip_data.total) {
                            total = tooltip_data.total;
                          } else {
                            total = payload
                              .filter(
                                // ignores the non stacked bars
                                (bar) => !nonStackedBars.includes(bar.dataKey)
                              )
                              .reduce((acc, cur) => acc + (cur.value || 0), 0);
                          }

                          const topContent = tooltip_data.topContent;
                          let IMAGE_TOP_CONTENT = {
                            backgroundImage: `url(${missing_image})`,
                          };

                          if (topContent) {
                            if (topContent.thumbnail)
                              IMAGE_TOP_CONTENT = {
                                backgroundImage: `url(${getImageSrc(
                                  topContent.thumbnail
                                )})`,
                              };

                            topContent.value = topContent.views;
                            topContent.trends.forEach((chartData) => {
                              chartData.value = chartData.views;
                              chartData.label = chartData.date;
                              // eslint-disable-next-line no-self-assign
                              chartData.selected = chartData.selected;
                            });
                          }

                          const topList =
                            this.props.data.topLists &&
                            this.props.data.topLists.find(
                              (d) =>
                                d.date.split("-").slice(1).join("/") ===
                                  tooltip_data.date ||
                                moment(d.date).format("MM/YYYY") ===
                                  tooltip_data.date
                            ).topList;

                          if (topList) {
                            if (topList.thumbnail)
                              IMAGE_TOP_CONTENT = {
                                backgroundImage: `url(${getImageSrc(
                                  topList.thumbnail
                                )})`,
                              };

                            topList.value = topList.total;
                          }

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {Object.keys(payload)
                                  .filter(
                                    (key) =>
                                      !nonStackedBars.includes(
                                        payload[key].dataKey
                                      )
                                  )
                                  .sort(
                                    (a, b) =>
                                      payload[b].value - payload[a].value
                                  )
                                  .map((key, index) => (
                                    <div
                                      className="standard-tooltip__item"
                                      key={key}
                                    >
                                      {payload[key].dataKey !== "insights" ? (
                                        <>
                                          <span
                                            className="standard-tooltip__icon"
                                            style={{
                                              background: payload[key].color,
                                            }}
                                          ></span>
                                          <span
                                            className={clsx(
                                              "standard-tooltip__key",
                                              "text-capitalize"
                                            )}
                                            style={{
                                              color: payload[key].color,
                                            }}
                                          >
                                            {payload[key].dataKey}{" "}
                                          </span>

                                          <span className="standard-tooltip__value">
                                            {isWidgetForRate
                                              ? selectFormat(
                                                  "percentage",
                                                  payload[key].value,
                                                  "0.00%"
                                                )
                                              : selectFormat(
                                                  "number",
                                                  payload[key].value
                                                )}
                                          </span>
                                        </>
                                      ) : null}
                                    </div>
                                  ))}
                                {payload.find((k) =>
                                  nonStackedBars.includes(k.dataKey)
                                ) ? null : (
                                  <>
                                    <div className="standard-tooltip__dilimiter1"></div>

                                    <div className="standard-tooltip__item">
                                      <span
                                        className={clsx(
                                          "standard-tooltip__key",
                                          "text-capitalize",
                                          "total"
                                        )}
                                      >
                                        Total{" "}
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {isWidgetForRate
                                          ? selectFormat(
                                              "percentage",
                                              total,
                                              "0.00%"
                                            )
                                          : selectFormat("number", total)}
                                      </span>
                                    </div>
                                  </>
                                )}

                                {nonStackedBars.map((bar) => {
                                  const barValue = payload.find(
                                    (v) => v.dataKey === bar
                                  );
                                  if (!barValue) return null;
                                  return (
                                    <Fragment key={bar}>
                                      <div className="standard-tooltip__dilimiter1"></div>
                                      <div className="standard-tooltip__item">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: barValue.color,
                                          }}
                                        ></span>
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize"
                                          )}
                                          style={{
                                            color: barValue.color,
                                          }}
                                        >
                                          {barValue.dataKey}{" "}
                                        </span>

                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            "number",
                                            barValue.value
                                          )}
                                        </span>
                                      </div>
                                    </Fragment>
                                  );
                                })}

                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}

                                {topContent && (
                                  <>
                                    <div className="mt-3 standard-tooltip__dilimiter2" />
                                    <div className="my-2 standard-tooltip__section-header">
                                      Top content on this day.
                                    </div>
                                    <div className="standard-tooltip__details--posts">
                                      <article className="content-table">
                                        <figure className="content-table__figure">
                                          <div
                                            className="content-table__img"
                                            style={IMAGE_TOP_CONTENT}
                                          />
                                        </figure>

                                        <section className="content-table__detail">
                                          <span className="standard-tooltip__value">
                                            {metricBar(topContent)}
                                          </span>
                                        </section>
                                      </article>

                                      <div className="standard-tooltip__text">
                                        {truncate(topContent.title, 200)}
                                      </div>
                                    </div>
                                  </>
                                )}

                                {topList && (
                                  <>
                                    <div className="mt-3 standard-tooltip__dilimiter2" />

                                    <div className="mt-3 mb-3 standard-tooltip__section-header">
                                      Top 5
                                    </div>
                                    {topList.map((item, i) => {
                                      return (
                                        <div
                                          key={i}
                                          className="mb-2 standard-tooltip__details--posts"
                                        >
                                          <article className="content-table">
                                            <figure className="content-table__figure">
                                              <div
                                                className="content-table__img"
                                                style={{
                                                  backgroundImage: `url(${getImageSrc(
                                                    item.thumbnail
                                                  )})`,
                                                }}
                                              />
                                            </figure>

                                            <section className="content-table__detail">
                                              <span className="standard-tooltip__value">
                                                {metricBar(item)}
                                              </span>
                                            </section>
                                          </article>

                                          <div className="standard-tooltip__text">
                                            {truncate(item.title, 200)}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              {this.renderBars(preparedData)}

              {getAllKeys(data, ["date"])
                .filter((k) => nonStackedBars.includes(k))
                .map((k) => (
                  <Line
                    key={k}
                    yAxisId="right"
                    type="monotone"
                    dataKey={k}
                    stroke="#868E96"
                    strokeWidth={1}
                    dot={false}
                    cursor="pointer"
                  />
                ))}
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                scale="auto"
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(d) => selectFormat("number", d)}
                tick={
                  <CustomizedYAxisTick
                    {...(isWidgetForRate
                      ? { formatType: "percentage", formatMask: "0.00%" }
                      : null)}
                  />
                }
              />

              <YAxis
                yAxisId="right"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickRight />}
                orientation="right"
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem", //(this.state.tooltip.y - 20) + 'px',
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              distinctTotals={nonStackedBars.filter((nsb) =>
                allKeys.includes(nsb)
              )}
              formatType={
                isWidgetForRate ? { type: "percentage", mask: "0.00%" } : null
              }
              showSummary={false}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default LeeActivations;
